import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { firstValueFrom, Observable } from 'rxjs';
import { environment } from '../../environments/environment';

@Injectable({
  providedIn: 'root'
})
export class UsersService {
  private http = inject(HttpClient);

  // Mapping to your environment file keys
  private apiServer = environment.authenticationServer;
  private authServer = environment.authenticationServer; // Using authServer for session clearing
  private locationUrl = environment.locationService;

  // --- User Fetching Methods ---

  GetActiveUsers(cacheValue?: any, avoidRCSA?: any): Promise<any> {
    const params = new HttpParams().set('avoidRCSA', 'true');
    return firstValueFrom(this.http.get<any>(`${this.apiServer}/api/user/activeusers`, { params }))
  }

  GetUser(): Promise<any> {
    return firstValueFrom(this.http.get<any>(`${this.apiServer}/api/user`));
  }

    // In login.service.ts
    getSessionStats(): Observable<any> {
      return this.http.get(`${this.authServer}/api/user/session/activity/stats`);
    }
  

  GetUserDetails(): Promise<any> {
    return firstValueFrom(this.http.get<any>(`${this.apiServer}/api/user/getUsers`));
  }

  GetUserById(): Promise<any> {
    return firstValueFrom(this.http.get<any>(`${this.apiServer}/api/user/userId/`));
  }

  GetUserInformationById(user_id: string | number): Promise<any> {
    return firstValueFrom(this.http.get<any>(`${this.apiServer}/api/user/${user_id}`));
  }

  GetUserDetailsByUserId(user_id: string | number): Promise<any> {
    return firstValueFrom(this.http.get<any>(`${this.apiServer}/api/user/details/user/${user_id}`))
      .then(res => res.data);
  }

  // --- Graph & Analytics ---

  GetUserGraph(columns: any): Promise<any> {
    let params = new HttpParams();

    // 1. Force the columns into an array (handles both arrays and comma-strings)
    let columnsArray: string[] = [];
    if (Array.isArray(columns)) {
      columnsArray = columns;
    } else if (typeof columns === 'string') {
      columnsArray = columns.split(',');
    }

    // 2. Safely APPEND each column individually
    columnsArray.forEach(col => {
      if (col && col.trim() !== '' && col.trim() !== 'undefined') {
        params = params.append('column', col.trim());
      }
    });

    return firstValueFrom(this.http.get<any>(`${this.apiServer}/api/user/graph`, { params }))
      .then(response => {
        // Unwrap the data to match legacy behavior
        return response.data || response;
      });
  }

  GetBlockedUserGraph(columns: any, condition_fields: any, condition_value: any): Promise<any> {
    let params = new HttpParams();

    // 1. Force the columns into an array 
    let columnsArray: string[] = [];
    if (Array.isArray(columns)) {
      columnsArray = columns;
    } else if (typeof columns === 'string') {
      columnsArray = columns.split(',');
    }

    // 2. Safely APPEND each column to recreate the exact working URL
    columnsArray.forEach(col => {
      if (col && col.trim() !== '' && col.trim() !== 'undefined') {
        params = params.append('column', col.trim());
      }
    });

    // 3. Append conditions
    if (condition_fields !== undefined && condition_fields !== null) {
      params = params.set('condition_field', condition_fields);
    }
    if (condition_value !== undefined && condition_value !== null) {
      params = params.set('condition_value', condition_value);
    }

    // Use your original apiServer variable here!
    return firstValueFrom(this.http.get<any>(`${this.apiServer}/api/user/graph`, { params }))
      .then(response => {
        return response.data || response;
      });
  }

  // --- User Management (Write) ---

  CreateUser(user: any): Promise<any> {
    return firstValueFrom(this.http.post<any>(`${this.apiServer}/api/user`, user));
  }

  UpdateUser(user: any): Promise<any> {
    return firstValueFrom(this.http.put<any>(`${this.apiServer}/api/user/userId/${user.id}`, user));
  }

  UpdateUserDetails(userDetails: any): Promise<any> {
    return firstValueFrom(this.http.put<any>(`${this.apiServer}/api/user/details/${userDetails.details_id}`, userDetails));
  }

  DeleteUser(user: any): Promise<any> {
    return firstValueFrom(this.http.post<any>(`${this.apiServer}/api/user/deleteUser/${user.user_id}`, {}));
  }

  DisableUser(user_id: string | number): Promise<any> {
    return firstValueFrom(this.http.put(`${this.apiServer}/api/user/disable/${user_id}`, {}));
  }

  EnableUser(user_id: string | number): Promise<any> {
    return firstValueFrom(this.http.put(`${this.apiServer}/api/user/enable/${user_id}`, {}));
  }

  UnblockUser(payload: any): Promise<any> {
    return firstValueFrom(this.http.put(`${this.apiServer}/api/user/unblock/${payload.id}`, payload));
  }

  ResetPassword(userDetails: any): Promise<any> {
    return firstValueFrom(this.http.put(`${this.apiServer}/api/user/passwordreset`, userDetails));
  }

  // --- Authorization & Roles ---

  GetAccessRoles(): Promise<any> {
    return firstValueFrom(this.http.get<any>(`${this.apiServer}/api/authorization/accessrole`));
  }

  GetAccessRolesByUser(user_id: string | number): Promise<any> {
    return firstValueFrom(this.http.get<any>(`${this.apiServer}/api/authorization/userrole/user_id/${user_id}`));
  }

  GetRolePermission(role_id: string | number, user_id: string | number): Promise<any> {
    return firstValueFrom(this.http.get<any>(`${this.apiServer}/api/authorization/accessrolepermission/role/${role_id}/user/${user_id}`));
  }

  GetAccessPermissionsByUser(user_id: string | number): Promise<any> {
    return firstValueFrom(this.http.get<any>(`${this.apiServer}/api/authorization/accessrolepermission/search/user_id-${user_id}`));
  }

  InsertUserRole(roleDetails: any): Promise<any> {
    return firstValueFrom(this.http.post<any>(`${this.apiServer}/api/authorization/userrole`, roleDetails));
  }

  InsertRolePermission(rolePermission: any): Promise<any> {
    return firstValueFrom(this.http.post<any>(`${this.apiServer}/api/authorization/rolepermission`, rolePermission));
  }

  GetPermissionsByRoleId(roleId: string | number): Promise<any> {
    return firstValueFrom(this.http.get<any>(`${this.apiServer}/api/authorization/accessroleandpermission/role_id/${roleId}`));
  }

  DeleteUserRole(user_role: any): Promise<any> {
    return firstValueFrom(this.http.delete<any>(`${this.apiServer}/api/authorization/userrole/${user_role.id}`, { body: user_role }));
  }

  // Add this method in your UsersService class
  getUserRole(userId: string): Promise<any> {
    return firstValueFrom(this.http.get<any>(`${this.authServer}/api/user/getuserRole/${userId}`));
  }

  // --- Sessions & Preferences ---

  ClearSessions(user_id: string | number): Promise<any> {
    return firstValueFrom(this.http.get(`${this.authServer}/api/user/clear/session/user/${user_id}`));
  }

  GetActiveSessions(): Promise<any> {
    return firstValueFrom(this.http.get<any>(`${this.apiServer}/api/user/activesessions`)).then(res => res.data);
  }

  GetSessionsForUser(user_id: string | number): Promise<any> {
    return firstValueFrom(this.http.get<any>(`${this.apiServer}/api/user/sessions/${user_id}`)).then(res => res.data);
  }

  GetPreferencesByUserId(userId: string | number): Promise<any> {
    return firstValueFrom(this.http.get<any>(`${this.apiServer}/api/preferences/user/${userId}`));
  }

  ResetPreferencesToDefault(userId: string | number): Promise<any> {
    return firstValueFrom(this.http.delete<any>(`${this.apiServer}/api/preferences/user/${userId}`));
  }

  // --- Location Services ---

  GetLocations(): Promise<any> {
    return firstValueFrom(this.http.get<any>(`${this.locationUrl}/api/location`)).then(res => res.data);
  }

  GetUserBaseLocations(userId: string | number): Promise<any> {
    return firstValueFrom(this.http.get<any>(`${this.apiServer}/api/user/${userId}/baselocation`)).then(res => res.data);
  }

  SetBaseLocation(userId: string | number, locationId: string | number): Promise<any> {
    return firstValueFrom(this.http.post(`${this.apiServer}/api/user/${userId}/baselocation/${locationId}`, {}));
  }

  RemoveBaseLocation(userId: string | number, locationId: string | number): Promise<any> {
    return firstValueFrom(this.http.delete(`${this.apiServer}/api/user/${userId}/baselocation/${locationId}`));
  }
}